import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Box, Grid, IconButton, Link, MenuItem, Switch, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { darken, lighten } from 'polished';
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import ForwardIcon from '../icons/ForwardIcon';
import StyledTextField from '../styledComponents/TextField';
import { UserContext } from '../utils/UserContext';
import { useAxios } from '../utils/useAxios';
import { LoadingButton } from '@mui/lab';
import _ from 'underscore';
import { useAuth } from 'react-oidc-context';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';

export default function MobileSettings({ setDarkMode }) {

    const theme = useTheme();
    const below732 = useMediaQuery("(max-height: 732px)");

    const [mode, setMode] = useState(false);
    const [language, setLanguage] = useState("de-DE");
    const [representation, setRepresentation] = useState("");
    const [representations, setRepresentations] = useState([]);
    const [virtualRepresentation, setVirtualRepresentation] = useState("");
    const [virtualRepresentations, setVirtualRepresentations] = useState([]);
    const [notificationType, setNotificationType] = useState(0);
    const [switchView, setSwitchView] = useState(false);

    const { user, setUser } = useContext(UserContext);

    const auth = useAuth();

    const navigate = useNavigate();

    const { t, i18n } = useTranslation();

    const axiosInstance = useAxios();

    useEffect(() => {
        if (!user.userID) {
            navigate("/dashboard");
        }
        document.title = t("vmm-settings");
        document.body.style.backgroundColor = theme.palette.background.default;
        sessionStorage.removeItem("redirect");
        sessionStorage.setItem("displayContent", true);
        if (localStorage.getItem("darkMode") === "true") {
            setMode(true);
        } else {
            setMode(false);
        }
        if (localStorage.getItem("i18nextLng") !== undefined) {
            setLanguage(localStorage.getItem("i18nextLng"));
        }
        if (user.userID !== "account-not-active" && representations?.length === 0) {
            fetchRepresentations();
        }
        if (user.userID !== "account-not-active" && virtualRepresentations?.length === 0) {
            fetchVirtualRepresentations();
        }
        if (user.userID !== "account-not-active") {
            setNotificationType(user.notificationType);
        }
        //eslint-disable-next-line
    }, [theme.palette.mode]);

    const changeMode = () => {
        localStorage.setItem("darkMode", !mode);
        setMode(!mode);
        setDarkMode(!mode);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    const fetchRepresentations = async () => {
        await axiosInstance.current.get("/representations", { "params": { "virtual": false } }).then(res => {
            setRepresentations([...res.data]);
            if (user.defaultProxy?.userID !== undefined) {
                setRepresentation(res.data.filter(r => r.userID === user.defaultProxy?.userID)[0]);
            }
        })
    };

    const setDefaultProxy = async (newRepresentation) => {
        await axiosInstance.current.post(`/proxy/${newRepresentation.email}`).then((res) => {
            setUser({
                ...user,
                defaultProxy: newRepresentation
            })
        }).catch(err => {
            console.error(err);
        })
        setRepresentation(newRepresentation);
    };

    const removeDefaultProxy = async () => {
        await axiosInstance.current.delete(`/proxy`).then((res) => {
            setUser({
                ...user,
                defaultProxy: undefined
            })
        }).catch(err => {
            console.error(err);
        })
        setRepresentation("");
    };

    const fetchVirtualRepresentations = async () => {
        await axiosInstance.current.get("/representations", { "params": { "virtual": true } }).then(res => {
            setVirtualRepresentations([...res.data]);
            if (user.defaultVirtualProxy?.userID !== undefined) {
                setVirtualRepresentation(res.data.filter(r => r.userID === user.defaultVirtualProxy?.userID)[0]);
            }
        })
    };

    const updateVirtualRepresentation = async (newVirtualRepresentation) => {
        await axiosInstance.current.post(`/virtualproxy/${newVirtualRepresentation.email}`).then((res) => {
            setUser({
                ...user,
                defaultVirtualProxy: newVirtualRepresentation
            })
        }).catch(err => {
            console.error(err);
        })
        setVirtualRepresentation(newVirtualRepresentation);
    };

    const updateNotification = async (newNotification) => {
        await axiosInstance.current.post(`/notificationtype/${newNotification}`).then((res) => {
            setUser({
                ...user,
                notificationType: newNotification
            })
        }).catch(err => {
            console.error(err);
        })
        setNotificationType(newNotification);
    };

    const handleLogout = () => {
        const clearStorage = () => {
            console.log("Clearing storage...");
            localStorage.removeItem("externalClientId");
            localStorage.removeItem("externalScope");
            localStorage.removeItem("authority");
            localStorage.removeItem("client");
            localStorage.removeItem("email");
            localStorage.removeItem("url");
            localStorage.removeItem("logo");
            localStorage.removeItem("primary");
            localStorage.removeItem("color");
            sessionStorage.clear();
            console.log("Storage cleared.");
        };

        // Check if user is authenticated with Microsoft Entra ID
        const isMicrosoftAuth = auth?.settings?.authority?.includes('microsoftonline.com');
        
        if (isMicrosoftAuth) {
            // Get tenant from the authority URL
            const authority = auth?.settings?.authority;
            const tenant = authority ? authority.split('/')[3] : '';
            const postLogoutRedirectUri = encodeURIComponent(window.location.origin + '/login');
            
            // First clear storage and do silent signout
            clearStorage();
            auth.signoutSilent().finally(() => {
                // Then redirect to Microsoft's logout endpoint
                window.location.href = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
            });
        } else {
            // Regular logout for non-Microsoft users
            auth.signoutSilent().then(() => {
                clearStorage();
                navigate("/login");
            });
        }
    };

    const GeneralView = () => {
        return (
            <Fragment>
                <Grid container direction="column" sx={{ pb: 1, borderBottom: 1, borderBottomColor: "primary.main" }}>
                    <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("presentation")}</Typography>
                    <Grid container sx={{ my: 1 }}>
                        <Box onClick={changeMode} sx={{ ml: 1.5, display: "inherit", justifyContent: "inherit" }}>
                            <Switch checked={mode} color="primary" size="small" />
                            <Typography sx={{ ml: 0.5, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("dark-mode")}</Typography>
                        </Box>
                    </Grid>
                    <Typography sx={{ ml: 2, mt: .5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("language")}</Typography>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={language || ""}
                        onChange={(event) => {
                            setLanguage(event.target.value)
                            changeLanguage(event.target.value);
                        }}
                        select
                        // InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none",
                                        mt: .5,
                                        width: "85%",
                                        maxWidth: 355
                                    }
                                }
                            }
                        }}
                        sx={{ px: 1.8, my: 1 }}
                        disabled={user.userID === "account-not-active"}
                    >
                        <MenuItem dense key={`lang-en`} value={"en-US"} style={{ whiteSpace: "normal" }}>
                            {t("english")}
                        </MenuItem>
                        <MenuItem dense key={`lang-de`} value={"de-DE"} style={{ whiteSpace: "normal" }}>
                            {t("german")}
                        </MenuItem>
                    </StyledTextField>
                    <Typography sx={{ ml: 2, mt: 1, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("default-proxy")}</Typography>
                    {representations.length < 20 ? (
                        <StyledTextField
                            variant="outlined"
                            size="small"
                            value={representation}
                            onChange={(event) => { setDefaultProxy(event.target.value); }}
                            select
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{
                                renderValue: (rep) => (<Typography>{`${rep.familyName} ${rep.surName}`}</Typography>),
                                MenuProps: {
                                    sx: {
                                        "& .MuiMenu-paper": {
                                            bgcolor: "background.default",
                                            backgroundImage: "none",
                                            mt: .5,
                                            width: "85%",
                                            maxWidth: 355
                                        }
                                    }
                                }
                            }}
                            sx={{ px: 1.8, my: 1 }}
                            disabled={user.userID === "account-not-active"}
                        >
                            {representations?.map((r, i) => (
                                <MenuItem dense key={`item-${i}`} value={r}>
                                    <Typography>
                                        {`${r.familyName} ${r.surName}`}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </StyledTextField>
                    ) : (
                        <Autocomplete
                            id="representation-autocomplete"
                            options={representations}
                            getOptionLabel={(option) => `${option.familyName} ${option.surName}`}
                            value={representation || null}
                            onChange={(event, newValue) => {
                                setDefaultProxy(newValue);
                            }}
                            renderInput={(params) => (
                                <StyledTextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ px: 1.8, my: 1 }}
                                    disabled={user.userID === "account-not-active"}
                                />
                            )}
                            disableClearable
                            isOptionEqualToValue={(option, value) => option.userID === value?.userID}
                            disabled={user.userID === "account-not-active"}
                        />
                    )}
                    <Typography sx={{ ml: 2, mt: 1, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("virtual-proxy")}</Typography>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={virtualRepresentation}
                        onChange={(event) => { updateVirtualRepresentation(event.target.value); }}
                        select
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            renderValue: (rep) => (<Typography>{`${rep.familyName} ${rep.surName}`}</Typography>),
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none",
                                        mt: .5,
                                        width: "85%",
                                        maxWidth: 355
                                    }
                                }
                            }
                        }}
                        sx={{ px: 1.8, my: 1 }}
                        disabled={user.userID === "account-not-active"}
                    >
                        {virtualRepresentations?.map((r, i) => (
                            <MenuItem dense key={`item-${i}`} value={r}>
                                <Typography>
                                    {`${r.familyName} ${r.surName}`}
                                </Typography>
                            </MenuItem>
                        ))}
                    </StyledTextField>
                    {/* <Typography sx={{ ml: 2, mt: 1, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("notification-type")}</Typography>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={notificationType}
                        onChange={(event) => { updateNotification(event.target.value); }}
                        select
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none",
                                        mt: .5,
                                        width: "85%",
                                        maxWidth: 355
                                    }
                                }
                            }
                        }}
                        sx={{ px: 1.8, my: 1 }}
                        disabled={user.userID === "account-not-active"}
                    >
                        <MenuItem dense key={"0"} value={0} style={{ whiteSpace: "normal" }}>
                            {t("no-notification")}
                        </MenuItem>
                        <MenuItem dense key={"2"} value={2} style={{ whiteSpace: "normal" }}>
                            {t("message-to-me")}
                        </MenuItem>
                        <MenuItem dense key={"4"} value={4} style={{ whiteSpace: "normal" }}>
                            {t("message-to-me-and-rep")}
                        </MenuItem>
                    </StyledTextField> */}
                    <IconButton onClick={() => { setSwitchView(true); }} sx={{ pl: 2, pr: 1.5, py: 0, my: 1, width: "100%", borderRadius: 6, justifyContent: "flex-start" }}>
                        <Typography sx={{ fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px", color: theme.palette.text.primary, ml: .5 }}>{t("personal-data")}</Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <ForwardIcon />
                    </IconButton>
                </Grid>
                <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
                    <Typography sx={{ mt: 1.5, fontSize: "20px", fontStyle: "normal", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>Tutorial</Typography>
                    <Link href="https://mobilboxmanager.info/mobilboxmanager_kurzanleitung/" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("short-instructions")}</Link>
                    <Link href="https://www.youtube.com/watch?v=kyzdjQCixIw" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("product-video")}</Link>
                </Grid>
                <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
                    <Typography sx={{ mt: 1.5, fontSize: "20px", fontStyle: "normal", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>Support</Typography>
                    <Typography sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("contact-support-text")}</Typography>
                    {/* <Link href="https://www.mobilboxmanager.info/" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>FAQ</Link> */}
                </Grid>
                <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
                    <Typography sx={{ mt: 1.5, fontSize: "20px", fontStyle: "normal", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>{t("legal")}</Typography>
                    <Link href="https://mobilboxmanager.info/impressum/" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("imprint")}</Link>
                    <Link href="https://mobilboxmanager.info/datenschutz/" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("data-protection")}</Link>
                    <Link href="https://www.gosh.at/" sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>Powered by gosh!</Link>
                </Grid>
                <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
                    <Typography sx={{ mt: 1.5, fontSize: "20px", fontStyle: "normal", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>{t("technical")}</Typography>
                    <Typography sx={{ mt: 1, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>Version {process.env.REACT_APP_VERSION || "X.X.X"}</Typography>
                </Grid>
                <Grid container direction="column" sx={{ px: 2, pb: 4, alignContent: "flex-start" }}>
                    <Grid container onClick={() => { handleLogout(); }} sx={{ mt: 3 }}>
                        <LogoutIcon sx={{ color: "error.main", fontSize: 18, mt: 0.25 }} />
                        <Typography sx={{ color: "error.main", ml: 0.5, fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>{t("logout")}</Typography>
                    </Grid>
                </Grid>
            </Fragment>
        );
    };

    const PersonalView = () => {

        const [update, setUpdate] = useState({ ...user });
        const [loading, setLoading] = useState(false);

        const save = async () => {
            setLoading(true);
            await axiosInstance.current({
                url: `/${user.userID}`,
                method: "PUT",
                data: update
            }).then(res => {
                if (res.status === 200) {
                    setUser({
                        ...res.data
                    });
                }
                setLoading(false);
            }).catch(err => {
                console.error(err.response);
                setLoading(false);
            })
        };

        return (
            <Grid container direction="column" sx={{ height: "100%", mt: 1, pb: 1 }}>
                <Grid container>
                    <Box onClick={() => { setSwitchView(false); }} sx={{ display: "inherit", justifyContent: "inherit" }}>
                        <IconButton sx={{ position: "relative", zIndex: theme.zIndex.appBar + 101 }}>
                            <ArrowBackIosNewIcon sx={{ color: "primary.main", fontSize: "18px" }} />
                        </IconButton>
                        <Typography sx={{ pt: .4, fontSize: "20px", fontStyle: "normal", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>{t("personal-data")}</Typography>
                    </Box>
                </Grid>
                <Typography sx={{ ml: 2, mt: 2, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>Email</Typography>
                <Tooltip title={t("contact-an-admin")} followCursor>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={user.email !== undefined ? user.email : " "}
                        sx={{ mx: 2, mt: 1 }}
                        disabled
                    />
                </Tooltip>
                <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("first-name")}</Typography>
                <Tooltip title={t("contact-an-admin")} followCursor>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={user.surName !== undefined ? user.surName : " "}
                        sx={{ mx: 2, mt: 1 }}
                        disabled
                    />
                </Tooltip>
                <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("surname")}</Typography>
                <Tooltip title={t("contact-an-admin")} followCursor>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={user.familyName !== undefined ? user.familyName : " "}
                        sx={{ mx: 2, mt: 1 }}
                        disabled
                    />
                </Tooltip>
                <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("phone")}</Typography>
                <Tooltip title={t("contact-an-admin")} followCursor>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={user.phoneNumber || ""}
                        sx={{ mx: 2, mt: 1 }}
                        disabled
                    />
                </Tooltip>
                <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("vpn-number")}</Typography>
                <Tooltip title={t("contact-an-admin")} followCursor>
                    <StyledTextField
                        variant="outlined"
                        size="small"
                        value={update.vpnNumber || ""}
                        sx={{ mx: 2, mt: 1 }}
                        disabled
                    />
                </Tooltip>
                <Typography sx={{ ml: 2, mt: 1.5, fontSize: "13px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.75px", color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary) }}>{t("voicemail-password")}</Typography>
                <StyledTextField
                    variant="outlined"
                    required
                    size="small"
                    error={!update.mobilboxPassword}
                    value={update.mobilboxPassword || ""}
                    onChange={(event) => {
                        setUpdate({ ...update, mobilboxPassword: event.target.value })
                    }}
                    sx={{ mx: 2, mt: 1, mb: 2 }}
                    disabled={user.userID === "account-not-active"}
                />
                <Grid container direction="column" sx={{ px: 2, alignItems: "flex-end" }}>
                    <Box sx={{ flexGrow: 1 }} />
                    <LoadingButton
                        variant="contained"
                        disabled={_.isEqual(user, update)}
                        loading={loading}
                        onClick={save}
                        sx={{
                            width: below732 ? "45%" : "40%",
                            position: below732 ? "relative" : "absolute",
                            bottom: below732 ? 0 : 20,
                            right: below732 ? 0 : 20,
                            mb: below732 ? 2 : 0,
                            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
                            textTransform: "none",
                            border: 1,
                            borderColor: _.isEqual(user, update) || loading ? "action.hover" : "primary.main",
                            borderRadius: 6,
                            "&:hover": { backgroundColor: "primary.dark" },
                            "&:disabled": { backgroundColor: "action.disableBackground" }
                        }}
                    >
                        <Typography sx={{ color: _.isEqual(user, update) ? "text.primary" : "primary.contrastText", fontSize: 16, fontWeight: _.isEqual(user, update) ? 400 : 700, visibility: loading ? "hidden" : "visible" }}>{t("save")}</Typography>
                    </LoadingButton>
                </Grid>
            </Grid>
        );
    };

    return (
        <Fragment>
            <Grid container sx={{ pl: 1, pb: 1.5, borderBottom: 1, borderBottomColor: "primary.main" }}>
                <IconButton onClick={() => (navigate("/dashboard"))} sx={{ mt: -3.5, position: "relative", zIndex: theme.zIndex.appBar + 101, display: switchView ? "none" : "inline-flex" }}>
                    <ArrowBackIosNewIcon sx={{ color: "primary.main", fontSize: "28px" }} />
                </IconButton>
                <Typography sx={{ ml: .5, mt: -3, fontSize: "30px", fontStyle: "normal", fontWeight: 700, lineHeight: "34px", letterSpacing: "-0.75px", position: "relative", zIndex: theme.zIndex.appBar + 101 }}>{t("settings")}</Typography>
            </Grid>
            {switchView ? <PersonalView /> : <GeneralView />}
        </Fragment>
    );
};